// src/pages/trading-portal/TradeExecution/ExecutionAlgorithmsPage.js
// Execution Algorithms - tabbed interface for algorithm configurations and executions

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tabs,
    Tab,
    Tooltip,
    Skeleton,
    Snackbar,
    Switch,
    FormControlLabel,
    LinearProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
    Cancel as CancelIcon,
    Memory as MemoryIcon,
    Settings as SettingsIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    AlgoType,
    AlgoTypeLabels,
    AlgoTypeColors,
    UrgencyLevel,
    UrgencyLevelLabels,
    UrgencyLevelColors,
    BenchmarkType,
    BenchmarkTypeLabels,
    AlgoExecStatus,
    AlgoExecStatusLabels,
    AlgoExecStatusColors,
    IndicatorType,
    IndicatorTypeLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const ExecutionAlgorithmsPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Global state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState(null);

    // ==================== TAB 0: ALGORITHM CONFIGURATIONS ====================
    const [algorithms, setAlgorithms] = useState([]);
    const [algosLoading, setAlgosLoading] = useState(true);
    const [algosPage, setAlgosPage] = useState(0);
    const [algosPerPage, setAlgosPerPage] = useState(10);
    const [algosTotal, setAlgosTotal] = useState(0);
    const [algoDialog, setAlgoDialog] = useState(false);
    const [algoForm, setAlgoForm] = useState({});
    const [algoEditing, setAlgoEditing] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // ==================== TAB 1: EXECUTIONS ====================
    const [executions, setExecutions] = useState([]);
    const [execLoading, setExecLoading] = useState(false);
    const [execPage, setExecPage] = useState(0);
    const [execPerPage, setExecPerPage] = useState(10);
    const [execTotal, setExecTotal] = useState(0);
    const [execFilterStatus, setExecFilterStatus] = useState('');
    const [execFilterAlgoId, setExecFilterAlgoId] = useState('');
    const [actionLoading, setActionLoading] = useState(null);

    // Execution action confirmation dialog state (Issue 10)
    const [execActionDialog, setExecActionDialog] = useState(false);
    const [pendingExecAction, setPendingExecAction] = useState(null);
    const [pendingExecId, setPendingExecId] = useState(null);

    // ==================== PERMISSIONS ====================
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-tee-algorithms', 'view');
            const add = await hasPermission('tr-tee-algorithms', 'add');
            const edit = await hasPermission('tr-tee-algorithms', 'edit');
            const del = await hasPermission('tr-tee-algorithms', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // ==================== TAB 0: ALGORITHM CONFIGURATIONS ====================
    const fetchAlgorithms = useCallback(async () => {
        if (!canView) return;
        setAlgosLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: algosPage + 1, per_page: algosPerPage });
            const response = await axiosInstance.get(`/api/trade-execution/algorithms?${params.toString()}`);
            if (response.data.success) {
                setAlgorithms(response.data.data || []);
                setAlgosTotal(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch algorithms');
        } finally {
            setAlgosLoading(false);
        }
    }, [canView, algosPage, algosPerPage]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchAlgorithms();
    }, [fetchAlgorithms, canView, activeTab]);

    const handleAlgoDialogOpen = (algo = null) => {
        if (algo) {
            setAlgoEditing(algo);
            setAlgoForm({
                name: algo.name || '',
                algo_type: algo.algo_type || '',
                description: algo.description || '',
                urgency_level: algo.urgency_level || '',
                max_duration_minutes: algo.max_duration_minutes || '',
                max_participation_rate: algo.max_participation_rate || '',
                min_fill_rate: algo.min_fill_rate || '',
                benchmark_type: algo.benchmark_type || '',
                dark_pool_eligible: algo.dark_pool_eligible || false,
                ml_optimization_enabled: algo.ml_optimization_enabled || false,
                indicator_conditions: algo.default_params?.indicator_conditions || [],
            });
        } else {
            setAlgoEditing(null);
            setAlgoForm({
                name: '',
                algo_type: '',
                description: '',
                urgency_level: '',
                max_duration_minutes: '',
                max_participation_rate: '',
                min_fill_rate: '',
                benchmark_type: '',
                dark_pool_eligible: false,
                ml_optimization_enabled: false,
                indicator_conditions: [],
            });
        }
        setAlgoDialog(true);
    };

    const handleAlgoSave = async () => {
        // Issue 20: Required field validation
        if (!algoForm.algo_type) {
            setSnackbar({ message: 'Algorithm Type is required', severity: 'error' });
            return;
        }
        if (!algoForm.urgency_level) {
            setSnackbar({ message: 'Urgency Level is required', severity: 'error' });
            return;
        }
        setSubmitting(true);
        try {
            const { indicator_conditions, ...formData } = algoForm;
            const payload = {
                ...formData,
                max_duration_minutes: algoForm.max_duration_minutes ? Number(algoForm.max_duration_minutes) : null,
                max_participation_rate: algoForm.max_participation_rate ? Number(algoForm.max_participation_rate) : null,
                min_fill_rate: algoForm.min_fill_rate ? Number(algoForm.min_fill_rate) : null,
                default_params: {
                    indicator_conditions: (indicator_conditions || []).filter(c => c.indicator_type && c.threshold !== ''),
                },
            };
            if (algoEditing) {
                await axiosInstance.put(`/api/trade-execution/algorithms/${algoEditing.id}`, payload);
                setSnackbar({ message: 'Algorithm updated successfully', severity: 'success' });
            } else {
                await axiosInstance.post('/api/trade-execution/algorithms', payload);
                setSnackbar({ message: 'Algorithm created successfully', severity: 'success' });
            }
            setAlgoDialog(false);
            fetchAlgorithms();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save algorithm');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteOpen = (algo) => {
        setDeleteTarget(algo);
        setDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setSubmitting(true);
        try {
            await axiosInstance.delete(`/api/trade-execution/algorithms/${deleteTarget.id}`);
            setSnackbar({ message: 'Algorithm deleted successfully', severity: 'success' });
            setDeleteDialog(false);
            setDeleteTarget(null);
            fetchAlgorithms();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete algorithm');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== TAB 1: EXECUTIONS ====================
    const fetchExecutions = useCallback(async () => {
        if (!canView) return;
        setExecLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: execPage + 1, per_page: execPerPage });
            if (execFilterStatus) params.append('status', execFilterStatus);
            if (execFilterAlgoId) params.append('algo_id', execFilterAlgoId);
            const response = await axiosInstance.get(`/api/trade-execution/algo-executions?${params.toString()}`);
            if (response.data.success) {
                setExecutions(response.data.data || []);
                setExecTotal(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch executions');
        } finally {
            setExecLoading(false);
        }
    }, [canView, execPage, execPerPage, execFilterStatus, execFilterAlgoId]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchExecutions();
    }, [fetchExecutions, canView, activeTab]);

    const handleExecAction = (execId, action) => {
        setPendingExecId(execId);
        setPendingExecAction(action);
        setExecActionDialog(true);
    };

    const handleExecActionConfirm = async () => {
        if (!pendingExecId || !pendingExecAction) return;
        setExecActionDialog(false);
        setActionLoading(pendingExecId);
        try {
            await axiosInstance.post(`/api/trade-execution/algo-executions/${pendingExecId}/${pendingExecAction}`);
            setSnackbar({ message: `Execution ${pendingExecAction}d successfully`, severity: 'success' });
            fetchExecutions();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.message || `Failed to ${pendingExecAction} execution`, severity: 'error' });
        } finally {
            setActionLoading(null);
            setPendingExecId(null);
            setPendingExecAction(null);
        }
    };

    // ==================== RENDER: TAB 0 - ALGORITHM CONFIGURATIONS ====================
    const renderAlgorithms = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Algorithm Configurations
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<RefreshIcon />} onClick={fetchAlgorithms} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={() => handleAlgoDialogOpen()}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Add Algorithm
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {algosLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Name</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Urgency</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Max Duration</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Max Participation %</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Benchmark</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Dark Pool</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>ML</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Active</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {algorithms.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                                <SettingsIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No algorithm configurations found</Typography>
                                                <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>Create a new algorithm to get started</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : algorithms.map((algo) => (
                                        <TableRow key={algo.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {algo.name || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={AlgoTypeLabels[algo.algo_type] || algo.algo_type}
                                                    size="small"
                                                    color={AlgoTypeColors[algo.algo_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={UrgencyLevelLabels[algo.urgency_level] || algo.urgency_level}
                                                    size="small"
                                                    color={UrgencyLevelColors[algo.urgency_level] || 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {algo.max_duration_minutes != null ? `${algo.max_duration_minutes} min` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {algo.max_participation_rate != null ? `${algo.max_participation_rate}%` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {BenchmarkTypeLabels[algo.benchmark_type] || algo.benchmark_type || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={algo.dark_pool_eligible ? 'Yes' : 'No'}
                                                    size="small"
                                                    color={algo.dark_pool_eligible ? 'success' : 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={algo.ml_optimization_enabled ? 'Yes' : 'No'}
                                                    size="small"
                                                    color={algo.ml_optimization_enabled ? 'info' : 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={algo.is_active ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={algo.is_active ? 'success' : 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {canEdit && (
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => handleAlgoDialogOpen(algo)}>
                                                                <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canDelete && (
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => handleDeleteOpen(algo)}>
                                                                <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={algosTotal}
                            page={algosPage}
                            onPageChange={(e, p) => setAlgosPage(p)}
                            rowsPerPage={algosPerPage}
                            onRowsPerPageChange={(e) => { setAlgosPerPage(parseInt(e.target.value, 10)); setAlgosPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 1 - EXECUTIONS ====================
    const renderExecutions = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Executions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontFamily }}>Status</InputLabel>
                        <Select
                            value={execFilterStatus}
                            onChange={(e) => { setExecFilterStatus(e.target.value); setExecPage(0); }}
                            label="Status"
                            sx={{ fontFamily }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {Object.entries(AlgoExecStatus).map(([key, value]) => (
                                <MenuItem key={key} value={value}>{AlgoExecStatusLabels[key]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel sx={{ fontFamily }}>Algorithm</InputLabel>
                        <Select
                            value={execFilterAlgoId}
                            onChange={(e) => { setExecFilterAlgoId(e.target.value); setExecPage(0); }}
                            label="Algorithm"
                            sx={{ fontFamily }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {algorithms.map((algo) => (
                                <MenuItem key={algo.id} value={algo.id}>{algo.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={fetchExecutions} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {execLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Algorithm Name</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Order ID</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Status</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Target Qty</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Executed Qty</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Progress</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Avg Price</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Benchmark Price</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>IS (bps)</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Started</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {executions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                                <HistoryIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No executions found</Typography>
                                                <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>Executions will appear here when algorithms are running</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : executions.map((exec) => (
                                        <TableRow key={exec.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {exec.algorithm_name || exec.algo_name || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {exec.order_id || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={AlgoExecStatusLabels[exec.status] || exec.status}
                                                    size="small"
                                                    color={AlgoExecStatusColors[exec.status] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {exec.target_quantity != null ? exec.target_quantity.toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {exec.executed_quantity != null ? exec.executed_quantity.toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell>
                                                {exec.target_quantity != null && exec.executed_quantity != null ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(exec.executed_quantity / exec.target_quantity) * 100}
                                                            sx={{
                                                                width: 60,
                                                                height: 5,
                                                                borderRadius: 3,
                                                                bgcolor: '#e2e8f0',
                                                                '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 },
                                                            }}
                                                        />
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                            {exec.executed_quantity}/{exec.target_quantity}
                                                        </Typography>
                                                    </Box>
                                                ) : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {exec.avg_price != null ? `$${Number(exec.avg_price).toFixed(2)}` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {exec.benchmark_price != null ? `$${Number(exec.benchmark_price).toFixed(2)}` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: exec.implementation_shortfall_bps > 0 ? '#ef4444' : exec.implementation_shortfall_bps < 0 ? '#10b981' : '#1e293b', fontSize: '0.82rem' }}>
                                                {exec.implementation_shortfall_bps != null ? exec.implementation_shortfall_bps.toFixed(1) : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {exec.started_at ? new Date(exec.started_at).toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {exec.status === 'PENDING' && canEdit && (
                                                        <Tooltip title="Start">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleExecAction(exec.id, 'start')}
                                                                disabled={actionLoading === exec.id}
                                                            >
                                                                {actionLoading === exec.id ? <CircularProgress size={16} /> : <PlayArrowIcon sx={{ fontSize: 18, color: '#10b981' }} />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {exec.status === 'RUNNING' && canEdit && (
                                                        <Tooltip title="Pause">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleExecAction(exec.id, 'pause')}
                                                                disabled={actionLoading === exec.id}
                                                            >
                                                                {actionLoading === exec.id ? <CircularProgress size={16} /> : <PauseIcon sx={{ fontSize: 18, color: '#f59e0b' }} />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {exec.status === 'PAUSED' && canEdit && (
                                                        <Tooltip title="Resume">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleExecAction(exec.id, 'resume')}
                                                                disabled={actionLoading === exec.id}
                                                            >
                                                                {actionLoading === exec.id ? <CircularProgress size={16} /> : <PlayArrowIcon sx={{ fontSize: 18, color: '#10b981' }} />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(exec.status === 'RUNNING' || exec.status === 'PAUSED') && canEdit && (
                                                        <Tooltip title="Cancel">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleExecAction(exec.id, 'cancel')}
                                                                disabled={actionLoading === exec.id}
                                                            >
                                                                {actionLoading === exec.id ? <CircularProgress size={16} /> : <CancelIcon sx={{ fontSize: 18, color: '#ef4444' }} />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={execTotal}
                            page={execPage}
                            onPageChange={(e, p) => setExecPage(p)}
                            rowsPerPage={execPerPage}
                            onRowsPerPageChange={(e) => { setExecPerPage(parseInt(e.target.value, 10)); setExecPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== PERMISSION GUARD ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view Execution Algorithms.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MemoryIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Execution Algorithms
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Configure and monitor algorithmic execution strategies
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                    <Tab label="Algorithm Configurations" icon={<SettingsIcon />} iconPosition="start" />
                    <Tab label="Executions" icon={<HistoryIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderAlgorithms()}
            {activeTab === 1 && renderExecutions()}

            {/* Add/Edit Algorithm Dialog */}
            <Dialog open={algoDialog} onClose={() => setAlgoDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {algoEditing ? 'Edit Algorithm' : 'Add Algorithm'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Name"
                                size="small"
                                fullWidth
                                value={algoForm.name || ''}
                                onChange={(e) => setAlgoForm({ ...algoForm, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Algorithm Type</InputLabel>
                                <Select
                                    value={algoForm.algo_type || ''}
                                    label="Algorithm Type"
                                    onChange={(e) => setAlgoForm({ ...algoForm, algo_type: e.target.value })}
                                >
                                    {Object.values(AlgoType).map((t) => (
                                        <MenuItem key={t} value={t}>{AlgoTypeLabels[t]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                                value={algoForm.description || ''}
                                onChange={(e) => setAlgoForm({ ...algoForm, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Urgency Level</InputLabel>
                                <Select
                                    value={algoForm.urgency_level || ''}
                                    label="Urgency Level"
                                    onChange={(e) => setAlgoForm({ ...algoForm, urgency_level: e.target.value })}
                                >
                                    {Object.values(UrgencyLevel).map((u) => (
                                        <MenuItem key={u} value={u}>{UrgencyLevelLabels[u]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Max Duration Minutes"
                                size="small"
                                fullWidth
                                type="number"
                                value={algoForm.max_duration_minutes || ''}
                                onChange={(e) => setAlgoForm({ ...algoForm, max_duration_minutes: e.target.value })}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Max Participation Rate %"
                                size="small"
                                fullWidth
                                type="number"
                                value={algoForm.max_participation_rate || ''}
                                onChange={(e) => setAlgoForm({ ...algoForm, max_participation_rate: e.target.value })}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Min Fill Rate %"
                                size="small"
                                fullWidth
                                type="number"
                                value={algoForm.min_fill_rate || ''}
                                onChange={(e) => setAlgoForm({ ...algoForm, min_fill_rate: e.target.value })}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Benchmark Type</InputLabel>
                                <Select
                                    value={algoForm.benchmark_type || ''}
                                    label="Benchmark Type"
                                    onChange={(e) => setAlgoForm({ ...algoForm, benchmark_type: e.target.value })}
                                >
                                    {Object.values(BenchmarkType).map((b) => (
                                        <MenuItem key={b} value={b}>{BenchmarkTypeLabels[b]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={algoForm.dark_pool_eligible || false}
                                        onChange={(e) => setAlgoForm({ ...algoForm, dark_pool_eligible: e.target.checked })}
                                        color="warning"
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Dark Pool Eligible</Typography>}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={algoForm.ml_optimization_enabled || false}
                                        onChange={(e) => setAlgoForm({ ...algoForm, ml_optimization_enabled: e.target.checked })}
                                        color="info"
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>ML Optimization</Typography>}
                            />
                        </Grid>

                        {/* ── Indicator Conditions (Optional) ── */}
                        <Grid item xs={12}>
                            <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 1.5, mt: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.82rem', color: '#475569' }}>
                                        Indicator Conditions (Optional)
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setAlgoForm({
                                            ...algoForm,
                                            indicator_conditions: [
                                                ...(algoForm.indicator_conditions || []),
                                                { indicator_type: '', period: '14', condition: 'BELOW', threshold: '' }
                                            ]
                                        })}
                                        sx={{ fontFamily, textTransform: 'none', fontSize: '0.75rem', color: ACCENT }}
                                    >
                                        Add Condition
                                    </Button>
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', mb: 1 }}>
                                    Algorithm will only execute slices when ALL conditions are met.
                                </Typography>
                                {(algoForm.indicator_conditions || []).map((cond, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                        <FormControl size="small" sx={{ minWidth: 130 }}>
                                            <InputLabel sx={{ fontFamily, fontSize: '0.8rem' }}>Indicator</InputLabel>
                                            <Select
                                                value={cond.indicator_type || ''}
                                                label="Indicator"
                                                onChange={(e) => {
                                                    const updated = [...algoForm.indicator_conditions];
                                                    updated[idx] = { ...updated[idx], indicator_type: e.target.value };
                                                    setAlgoForm({ ...algoForm, indicator_conditions: updated });
                                                }}
                                                sx={{ fontFamily, fontSize: '0.8rem' }}
                                            >
                                                {Object.entries(IndicatorType).filter(([k]) => !['CUSTOM', 'WMA'].includes(k)).map(([key, val]) => (
                                                    <MenuItem key={key} value={val} sx={{ fontFamily, fontSize: '0.8rem' }}>{IndicatorTypeLabels[val] || val}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField size="small" label="Period" type="number"
                                            value={cond.period || ''}
                                            onChange={(e) => {
                                                const updated = [...algoForm.indicator_conditions];
                                                updated[idx] = { ...updated[idx], period: e.target.value };
                                                setAlgoForm({ ...algoForm, indicator_conditions: updated });
                                            }}
                                            sx={{ width: 80, fontFamily, '& input': { fontFamily, fontSize: '0.8rem' } }}
                                        />
                                        <FormControl size="small" sx={{ minWidth: 90 }}>
                                            <Select
                                                value={cond.condition || 'BELOW'}
                                                onChange={(e) => {
                                                    const updated = [...algoForm.indicator_conditions];
                                                    updated[idx] = { ...updated[idx], condition: e.target.value };
                                                    setAlgoForm({ ...algoForm, indicator_conditions: updated });
                                                }}
                                                sx={{ fontFamily, fontSize: '0.8rem' }}
                                            >
                                                <MenuItem value="ABOVE" sx={{ fontFamily }}>Above</MenuItem>
                                                <MenuItem value="BELOW" sx={{ fontFamily }}>Below</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField size="small" label="Threshold" type="number"
                                            value={cond.threshold ?? ''}
                                            onChange={(e) => {
                                                const updated = [...algoForm.indicator_conditions];
                                                updated[idx] = { ...updated[idx], threshold: e.target.value };
                                                setAlgoForm({ ...algoForm, indicator_conditions: updated });
                                            }}
                                            sx={{ width: 100, fontFamily, '& input': { fontFamily, fontSize: '0.8rem' } }}
                                        />
                                        <IconButton size="small" onClick={() => {
                                            const updated = algoForm.indicator_conditions.filter((_, i) => i !== idx);
                                            setAlgoForm({ ...algoForm, indicator_conditions: updated });
                                        }}>
                                            <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setAlgoDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAlgoSave}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : (algoEditing ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Algorithm Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Algorithm</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete algorithm "{deleteTarget?.name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={submitting}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Execution Action Confirmation Dialog (Issue 10) */}
            <Dialog open={execActionDialog} onClose={() => setExecActionDialog(false)}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Confirm {pendingExecAction ? pendingExecAction.charAt(0).toUpperCase() + pendingExecAction.slice(1) : ''}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to {pendingExecAction} this execution?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setExecActionDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={pendingExecAction === 'cancel' ? 'error' : 'primary'}
                        onClick={handleExecActionConfirm}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        {pendingExecAction ? pendingExecAction.charAt(0).toUpperCase() + pendingExecAction.slice(1) : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={!!snackbar}
                autoHideDuration={6000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert
                        onClose={() => setSnackbar(null)}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%', fontFamily }}
                    >
                        {snackbar.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default ExecutionAlgorithmsPage;
